import {
  arrayRemove,
  arrayUnion,
  collection,
  doc,
  DocumentReference,
  setDoc,
  updateDoc,
  writeBatch,
} from 'firebase/firestore';
import chunk from 'lodash/chunk';
import { userAsPromise } from '../providers/userProvider';
import { IAthlete, IGroup, IUser } from '../types';

/**
 * @description creates a new group
 * @param user
 * @param group
 */
const createGroup = async (
  user: IUser,
  group: IGroup,
  aggregateOnCreate: boolean
): Promise<IGroup> => {
  const groupRef = doc(
    collection(user.services.firestore, `orgs/${user.orgId}/groups`)
  );
  const docData = {
    ...group,
    id: groupRef.id,
    active: true,
    aggregateOnCreate,
  };
  return setDoc(groupRef, docData).then((): IGroup => {
    return docData;
  });
};

/**
 * @description update a group
 * @param user
 * @param group
 */
const updateGroup = async (user: IUser, group: IGroup): Promise<IGroup> => {
  const groupRef = doc(
    user.services.firestore,
    `orgs/${user.orgId}/groups/${group.id}`
  );
  const docData = { ...group };
  return setDoc(groupRef, docData, { merge: true }).then((): IGroup => {
    return docData;
  });
};

/**
 * @description archive a group
 * @param group
 */
const archiveGroup = async (group: IGroup): Promise<void> => {
  const user = await userAsPromise();

  await updateDoc(
    doc(user.services.firestore, `orgs/${user.orgId}/groups/${group.id}`),
    group as unknown as Record<string, unknown>
  );
};

/**
 * @description creates a group and optionally adds athletes to the group
 * @param group
 * @param athletes
 */
const createGroupWithAthletes = async (
  group: IGroup,
  includeHistorical: boolean,
  athletes?: string[]
): Promise<void[]> => {
  const user = await userAsPromise();

  /// create the group
  const createdGroup = await createGroup(user, group, includeHistorical);
  /// optionally add the athletes to the created group
  if (athletes?.length > 0) {
    /// perform updates in batches to avoid firestore write limit
    const updateBatches = chunk(athletes, 200);
    const batchOperations = updateBatches.map(
      (updatesInBatch: string[]): Promise<void> => {
        const refs = updatesInBatch.map(
          (id: string): DocumentReference =>
            doc(user.services.firestore, `orgs/${user.orgId}/athletes/${id}`)
        );
        const batch = writeBatch(user.services.firestore);

        for (const ref of refs) {
          batch.update(ref, {
            groups: arrayUnion(createdGroup.id),
          });
        }
        return batch.commit();
      }
    );
    return Promise.all(batchOperations);
  }
};

/**
 * @description creates a group and optionally adds athletes to the group
 * @param group
 * @param athletes
 */
const updateGroupWithAthletes = async (
  group: IGroup,
  selectedAthletes: string[],
  allAthletes: IAthlete[]
): Promise<void[]> => {
  const user = await userAsPromise();

  /// update the group
  const updatedGroup = await updateGroup(user, group);

  let batchOperations = [];
  /// update the athletes who are now members of the group
  if (selectedAthletes?.length > 0) {
    /// perform updates in batches to avoid firestore write limit
    const updateBatches = chunk(selectedAthletes, 200);
    const ops = updateBatches.map((updatesInBatch: string[]): Promise<void> => {
      const refs = updatesInBatch.map(
        (id: string): DocumentReference =>
          doc(user.services.firestore, `orgs/${user.orgId}/athletes/${id}`)
      );
      const batch = writeBatch(user.services.firestore);

      for (const ref of refs) {
        batch.update(ref, {
          groups: arrayUnion(updatedGroup.id),
        });
      }
      return batch.commit();
    });
    batchOperations = batchOperations.concat(ops);
  }

  /// determine if there any athletes that are no longer members of this group
  const nowExcludedAthletes = allAthletes
    .filter((a: IAthlete): boolean => {
      return !selectedAthletes.includes(a.id) && a.groups?.includes(group.id);
    })
    .map((a: IAthlete): string => a.id);

  if (nowExcludedAthletes.length > 0) {
    /// perform updates in batches to avoid firestore write limit
    const updateBatches = chunk(nowExcludedAthletes, 200);
    const ops = updateBatches.map((updatesInBatch: string[]): Promise<void> => {
      const refs = updatesInBatch.map(
        (id: string): DocumentReference =>
          doc(user.services.firestore, `orgs/${user.orgId}/athletes/${id}`)
      );
      const batch = writeBatch(user.services.firestore);

      for (const ref of refs) {
        batch.update(ref, {
          groups: arrayRemove(updatedGroup.id),
        });
      }
      return batch.commit();
    });
    batchOperations = batchOperations.concat(ops);
  }
  return Promise.all(batchOperations);
};

export {
  archiveGroup,
  createGroup,
  createGroupWithAthletes,
  updateGroup,
  updateGroupWithAthletes,
};
